import {doc} from '../docs.js';
import * as THREE from 'three';
import {intersect} from 'components/effects/tidal/intersect.js';
import {mod} from 'utils/mod.js';
import {pt_dist} from "effects/midi/mappable/convexHull.js";

const WIDTH = 280;
const RADIUS = (WIDTH/2)*.95;
const CENTER = (WIDTH/2);

doc(
    "jpolygons",
    {
        "tidals": "",
    },
    "Converts Jaki object tidals into polygons to be rendered",
    undefined,
    {
        tags: "Rhythm",
    }
)

const eq = (a,b) => {
    if (!a || !b) {
        return false;
    }
    for (let i=0; i < a.length; i++ ){
        if (a[i] !== b[i]) {
            return false;
        }
    }
    return true;
};

export const jpolygons = (object) => {
    // default width/height of 280px
    object.width = WIDTH;
    object.height = WIDTH;

    return ([tidals]) => {
        if (!tidals.some(x => x.needsUpdate)) {
            return undefined;
        } else {
            tidals.forEach(x => x.needsUpdate = false);
        }

        // given tidals do the work
        tidals = tidals.filter(x => x.inUse || x.lastUsed < 1);
        let radius = RADIUS;
        let factor = 2;
        let center = CENTER;
        let polygons = [];

        let nextRadius = undefined;
        for (let nth=0; nth < tidals.length; nth++) {
            let tidal = tidals[nth];
            let cycle = tidal.cycle;
            let RADIUS = nextRadius || Math.max(10, radius - 12*factor*(nth));
            
            let flattened = cycle; 
            let len = flattened.length;
            for (let i=0; i < flattened.length; i++) {
                let leaf = flattened[i];
                if (Array.isArray(leaf)) {
                    leaf = leaf[0];
                }
                let [_nextRadius, poly] = getConnectorPolygon(
                    center, i, len, cycle, RADIUS, {x:center, y:center}, leaf);
                if (poly) {
                    polygons.push(poly);
                }

                if (_nextRadius) {
                    nextRadius = _nextRadius;
                }

                if (leaf.type && leaf.type.type === '-' && leaf.type.value === 0) {
                    if (true || leaf.transpose !== null) {
                        let poly = getDashPolygon(center, leaf, i, RADIUS, cycle, len);
                        if (poly) {
                            polygons.push(poly);
                        }
                    }
                }
            }
        }
        return [polygons];
    };
};

const getConnectorPolygon = (center, a, len, cycle, radius, _center={x: center, y: center}, leaf, depth=0) => {
    if (leaf.transpose === null) {
        return [undefined, undefined];
    }
    let c0 = getLeafCoord(a, _center, radius, cycle);
    let c1 = getLeafCoord((a+1)%len, _center, radius, cycle);
    let c2 = getLeafCoord((a+2)%len, _center, radius, cycle);
    let c3 = getLeafCoord((a+3)%len, _center, radius, cycle);
    let c5 = getLeafCoord(mod(a-3,len), _center, radius, cycle);
    let c6 = getLeafCoord(mod(a-2,len), _center, radius, cycle);
    let c7 = getLeafCoord(mod(a-1,len), _center, radius, cycle);
    
    let p0 = c0;
    let p1 = intersect(c0, c3, c1, c6);
    let p2 = intersect(c7, c2, c6, c1);
    let p3 = intersect(c0, c5, c2, c7);
    
    
    let polygon = new Float32Array(8);
    polygon[0] = p0.x;
    polygon[1] = p0.y;
    polygon[2] = p1.x;
    polygon[3] = p1.y;
    polygon[4] = p2.x;
    polygon[5] = p2.y;
    polygon[6] = p3.x;
    polygon[7] = p3.y;
    let distance = pt_dist({x: center, y: center}, p2);
    return [distance, polygon];
}

const getDashPolygon = (center, leaf, a, radius, cycle, len) => {
    // draw arc
    let _center = {x: center, y: center};
    let c0 = getLeafCoord(a, _center, radius, cycle);
    let c1 = getLeafCoord((a+1)%len, _center, radius, cycle);
    let c2 = getLeafCoord((a+2)%len, _center, radius, cycle);
    let c3 = getLeafCoord((a+3)%len, _center, radius, cycle);
    let c5 = getLeafCoord(mod(a-3,len), _center, radius, cycle);
    let c6 = getLeafCoord(mod(a-2,len), _center, radius, cycle);
    let c7 = getLeafCoord(mod(a-1,len), _center, radius, cycle);
    
    //let p1 = intersect(c0, c3, c2, c7);
    let p3 = intersect(c0, c3, c1, c6);
    
    let polygon = new Float32Array(6);
    polygon[0] = c0.x;
    polygon[1] = c0.y;
    polygon[2] = p3.x;
    polygon[3] = p3.y;
    polygon[4] = c1.x;
    polygon[5] = c1.y;
    return polygon;
};

const getLeafCoord = (leafNumber, center, radius, cycle) => {
    let flattened = cycle.flat(16);
    let len = (cycle.length);
    let deg = (2*Math.PI * leafNumber/len) - 0.5*Math.PI;
    let x = center.x + radius * Math.cos(deg);
    let y = center.y + radius * Math.sin(deg);
    let c = {x,y,deg};
    return c;
};

doc(
    'jhits',
    {
        'trigger-routing': '',
        'tidals': '',
    },
    'Outputs coords of triggers',
    undefined,
    {
        inlets: 2
    }
);

export const jhits = (object, tidals) => {
    // keep track of currently playing triggers

    let currentTriggers = [];

    return (msg) => {
        let triggers = msg[0] && msg[0][1];
        if (!triggers) {//|| triggers.length === 0) {
            return;
        }

        let _tidals = tidals();
        if (!_tidals) {
            return;
        }

        let radii = getRadiiForTidals(_tidals);
        let coords = [];
        let ctr=0;
        if (triggers.length > 0) {
            triggers.map(x => 6 - x.outputNumber);
        }

        let _newCurrent =[];
        for (let oldTrigger of currentTriggers) {
            let {x,y,duration} = oldTrigger;
            duration -= 0.5;
            oldTrigger.duration = duration;
            if (duration > 0) {
                coords.push(x);
                coords.push(y);
                _newCurrent.push(oldTrigger);
            }
        }

        currentTriggers = _newCurrent;

        for (let trigger of triggers) {
            // not sure why i need to do this
            let tidalNumber = trigger.outputNumber - 1;
            let {x,y} = getLeafCoord(
                trigger.stepData.leafNumber,
                {x: CENTER, y: CENTER},
                radii[tidalNumber],
                _tidals[tidalNumber].cycle,
                true);
            coords.push(x);
            coords.push(y);
            currentTriggers.push({x,y, duration: trigger.stepData.duration});
        }
        return Float32Array.from(coords);
    };
};

export const getRadiiForTidals = (tidals) => {
    let nextRadius = undefined;
    let factor = 2;
    let radii = [];
    for (let nth=0; nth < tidals.length; nth++) {
        let tidal = tidals[nth];
        let cycle = tidal.cycle;
        let _radius = nextRadius || Math.max(10, RADIUS - 12*factor*(nth));

        radii.push(_radius);
        
        let flattened = cycle; 
        let len = flattened.length;
        
        for (let i=0; i < flattened.length; i++) {
            let leaf = flattened[i];
            if (Array.isArray(leaf)) {
                leaf = leaf[0];
            }
            let [_nextRadius, poly] = getConnectorPolygon(
                CENTER, i, len, cycle, _radius, {x:CENTER, y:CENTER}, leaf);
            if (_nextRadius) {
                nextRadius = _nextRadius;
            }
        }
    }
    return radii;
};



doc(
    "jclock",
    {
        "tidals": ""
    },
    "Returns meshes for the clock hands",
    undefined,
    {
        inlets: 1
    });
export const jclock = (object) => {

    const material = new THREE.LineBasicMaterial( { color : 0xffffff} );

    let meshes = [];
    return ([tidals]) => {
        if (!tidals) {
            return;
        }

        let radii = getRadiiForTidals(tidals);
        for (let i=0; i < tidals.length; i++) {
            let tidal = tidals[i];
            let leaves = tidal.cycle.flat(16);
            for (let leafNumber=0; leafNumber < leaves.length; leafNumber++) {
                let leaf = leaves[leafNumber];
                if (tidal.currentCycles && tidal.currentCycles[leaf.id]) {
                    
                    let {deg} = getLeafCoord(leafNumber, {x: CENTER, y: CENTER}, radii[i], leaves);
                    const curve = new THREE.EllipseCurve(
	                CENTER,  CENTER,            // ax, aY
	                radii[i], radii[i],           // xRadius, yRadius
	                1.5*Math.PI,  deg,  // aStartAngle, aEndAngle
	                false,            // aClockwise
	                0                 // aRotation
                    );
                    var points = curve.getSpacedPoints( leafNumber );
                    
                    if (!meshes[i]) {
                        var geometry = new THREE.BufferGeometry().setFromPoints(points);
                        var mesh = new THREE.Line( geometry, material);
                        meshes[i] = mesh;
                    } else {
                        meshes[i].geometry.setFromPoints(points);
                        meshes[i].geometry.attributes.position.needsUpdate = true;
                    }
                    break;
                }
            }
        }
        return [meshes];
    };
};
